// Iter 307 — Scanner barcode mockup untuk operator (HP kamera).
// Mendukung Code 128 (barcode mockup Iter 305) + QR (nota lama). Setiap decoded
// code memanggil POST /api/orders/{number}/barcode-scan sehingga perpindahan
// barang antar tim tercatat. UI menampilkan "Sekarang di: Tim X · PIC Y".
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import axios from "axios";
import { Camera, X, Keyboard, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { notify } from "./modal_center";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_ID = "scan-mockup-region";

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
];

// Iter 308 — Beep 150ms via Web Audio API agar operator tahu scan berhasil
//   meski layar HP tidak dilihat. Tanpa asset mp3 untuk hindari caching & gagal muat.
//   AudioContext di-resume saat user gesture (tombol "Mulai Scan") sehingga sesuai
//   autoplay policy browser.
let _scanAudioCtx = null;
function _getAudioCtx() {
  try {
    if (_scanAudioCtx) return _scanAudioCtx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _scanAudioCtx = new Ctx();
    return _scanAudioCtx;
  } catch { return null; }
}
export function playScanBeep({ freq = 1180, durationMs = 150, kind = "ok" } = {}) {
  const ctx = _getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* noop */ } }
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = kind === "err" ? "sawtooth" : "square";
    osc.frequency.setValueAtTime(kind === "err" ? 240 : freq, now);
    // envelope kecil agar tidak "click"
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.22, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.02);
  } catch { /* noop */ }
}

export default function ScanMockupModal({ open, onClose, operator, user, onOpenOrder, searchOnly, title }) {
  const [running, setRunning] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null); // last scan response
  // Iter 513 — Diagnostik ringan (hanya di mode searchOnly, non-persisten) untuk
  //   membantu Owner melakukan bring-your-own-device test di iPhone. Tidak ada
  //   data yang ditulis ke log produksi — semua state hidup di memori modal.
  const [diag, setDiag] = useState({
    frames: 0,          // jumlah frame yang di-feed ke decoder (approx dari onError count + success)
    decodeErrors: 0,    // frame yang gagal decode (bukti decoder aktif memproses)
    lastErrAt: "",      // waktu error terakhir (jam:menit:detik)
    videoW: 0,          // resolusi lebar stream aktif
    videoH: 0,          // resolusi tinggi stream aktif
    nativeDetector: "", // "supported" | "unavailable" | "code128-not-listed"
    nativeFormats: [],  // list format yang didukung BarcodeDetector.getSupportedFormats()
    callbackCount: 0,   // berapa kali onOpenOrder berhasil dipanggil
  });
  const diagRef = useRef(diag);
  const bumpDiag = (patch) => {
    diagRef.current = { ...diagRef.current, ...patch };
    setDiag(diagRef.current);
  };
  const scannerRef = useRef(null);
  const lastCodeRef = useRef({ code: "", at: 0 });
  // Iter 512 — Simpan handler terbaru dalam ref agar callback decoder (yang
  //   di-bind sekali saat start) selalu memanggil versi terbaru dari onOpenOrder
  //   / searchOnly. Tanpa ini, closure menangkap nilai render pertama.
  const searchOnlyRef = useRef(searchOnly);
  const onOpenOrderRef = useRef(onOpenOrder);
  useEffect(() => { searchOnlyRef.current = searchOnly; }, [searchOnly]);
  useEffect(() => { onOpenOrderRef.current = onOpenOrder; }, [onOpenOrder]);

  // Iter 513 — Deteksi native BarcodeDetector + format yang didukungnya pada
  //   open. iOS Safari (hingga 17.x) TIDAK mengekspos `window.BarcodeDetector`
  //   → fallback ZXing WASM selalu aktif di iPhone. Diagnostik ini membedakan
  //   Chrome Android (native path) vs Safari iPhone (WASM path).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const BD = window.BarcodeDetector;
        if (!BD) {
          bumpDiag({ nativeDetector: "unavailable", nativeFormats: [] });
          return;
        }
        let formats = [];
        try { formats = await BD.getSupportedFormats(); } catch { formats = []; }
        const hasCode128 = formats.map(f => String(f).toLowerCase()).includes("code_128");
        bumpDiag({
          nativeDetector: hasCode128 ? "supported" : "code128-not-listed",
          nativeFormats: formats,
        });
      } catch {
        bumpDiag({ nativeDetector: "unavailable", nativeFormats: [] });
      }
    })();
  }, [open]);

  const stop = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* noop */ }
      try { scannerRef.current.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setRunning(false);
  };

  // Iter 512 — Mode SEARCH-ONLY (dipakai Operator Bordir Offline + Shopee):
  //   decoder hanya mengekstrak teks nomor pesanan, mem-forward ke onOpenOrder
  //   (yang akan mengisi field pencarian), lalu menutup modal. Tidak ada POST
  //   ke /barcode-scan sehingga TIDAK membuat event perpindahan tim / tidak
  //   mengubah stage/PIC/status.
  const handleDecodedForSearch = async (raw) => {
    const code = String(raw || "").trim().toUpperCase();
    if (!code) return;
    const now = Date.now();
    if (lastCodeRef.current.code === code && now - lastCodeRef.current.at < 2000) return;
    lastCodeRef.current = { code, at: now };
    // Beep + vibrate untuk feedback cepat (tidak menunggu backend).
    try {
      playScanBeep({ freq: 1180, durationMs: 150 });
      if (window.navigator?.vibrate) window.navigator.vibrate(60);
    } catch { /* noop */ }
    bumpDiag({ callbackCount: (diagRef.current.callbackCount || 0) + 1 });
    try { onOpenOrderRef.current?.(code); } catch { /* noop */ }
    try { await stop(); } catch { /* noop */ }
    onClose?.();
  };

  const sendScan = async (raw) => {
    const code = String(raw || "").trim();
    if (!code || busy) return;
    // Iter 512 — Jika modal dibuka dalam mode search-only, lewatkan POST
    //   /barcode-scan sepenuhnya dan panggil callback pencarian saja.
    if (searchOnlyRef.current) {
      await handleDecodedForSearch(code);
      return;
    }
    // Guard client-side dedup 2s untuk cegah flood antar frame.
    const now = Date.now();
    if (lastCodeRef.current.code === code && now - lastCodeRef.current.at < 2000) return;
    lastCodeRef.current = { code, at: now };
    setBusy(true); setErrMsg("");
    try {
      const device = navigator.userAgent?.slice(0, 60) || "";
      const r = await axios.post(
        `${API}/orders/${encodeURIComponent(code)}/barcode-scan`,
        { device },
        { withCredentials: true }
      );
      setResult(r.data);
      // Iter 308 — Beep 150ms + vibrate saat scan tercatat (bukan deduped).
      //   Deduped tetap vibrate pendek supaya operator tahu scan dikenali meski
      //   tidak dicatat ulang.
      try {
        if (r?.data?.deduped) {
          playScanBeep({ freq: 740, durationMs: 90 });
          if (window.navigator?.vibrate) window.navigator.vibrate(30);
        } else {
          playScanBeep({ freq: 1180, durationMs: 150 });
          if (window.navigator?.vibrate) window.navigator.vibrate(60);
        }
      } catch { /* noop */ }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409 && e?.response?.data?.detail?.code === "channel_mismatch") {
        setErrMsg(e.response.data.detail.message);
      } else if (status === 404) {
        setErrMsg(`Nomor "${code}" tidak terdaftar di Offline maupun Shopee.`);
      } else if (status === 401 || status === 403) {
        setErrMsg(e?.response?.data?.detail || "Sesi habis atau tidak berwenang scan.");
      } else {
        setErrMsg(e?.response?.data?.detail || "Gagal mencatat scan.");
      }
      // Iter 308 — Beep error 150ms (nada rendah) agar jelas berbeda dari sukses.
      try {
        playScanBeep({ kind: "err", durationMs: 150 });
        if (window.navigator?.vibrate) window.navigator.vibrate([40, 40, 40]);
      } catch { /* noop */ }
    } finally { setBusy(false); }
  };

  const start = async () => {
    setErrMsg(""); setRunning(true); setResult(null);
    bumpDiag({ frames: 0, decodeErrors: 0, lastErrAt: "", videoW: 0, videoH: 0, callbackCount: 0 });
    // Iter 308 — Prime AudioContext di dalam user gesture (klik tombol) supaya
    //   beep sukses pertama tidak diblokir autoplay policy di iOS/Android.
    try {
      const ctx = _getAudioCtx();
      if (ctx && ctx.state === "suspended") { await ctx.resume().catch(()=>{}); }
    } catch { /* noop */ }
    try {
      const el = document.getElementById(REGION_ID);
      if (!el) throw new Error("Region scanner tidak siap");
      // Iter 513 — Mode searchOnly khusus: batasi SUPPORTED_FORMATS hanya ke
      //   Code 128 agar ZXing MultiFormatReader fokus pada 1 format saja.
      //   Barcode mockup TAMFARIS dicetak via `react-barcode` format="CODE128"
      //   (App.js:2824). Membatasi format ↓↓↓ latency decoder dan ↑↑↑ akurasi
      //   pada frame low-contrast / small barcode. Mode non-search (lama) tetap
      //   multi-format supaya kompatibel QR fallback.
      const formats = searchOnlyRef.current
        ? [Html5QrcodeSupportedFormats.CODE_128]
        : SUPPORTED_FORMATS;
      // Iter 513 — Aktifkan native BarcodeDetector (Chrome 83+ / Android). Pada
      //   Safari iPhone API ini tidak tersedia → html5-qrcode otomatis fallback
      //   ke ZXing WASM. Diagnostik di atas sudah memberitahu Owner path mana
      //   yang aktif.
      const q = new Html5Qrcode(REGION_ID, {
        verbose: false,
        formatsToSupport: formats,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      scannerRef.current = q;

      // Iter 513 — Konfigurasi start: pakai MediaTrackConstraints (bukan
      //   deviceId exact) agar Safari iOS bebas memilih back camera terbaik.
      //   Minta resolusi tinggi 1920x1080 (ideal) supaya garis Code 128 tipis
      //   TAMFARIS (bar width=1.4) ter-resolve dengan jelas. Safari akan
      //   downscale otomatis bila device tidak support — tidak ada
      //   OverconstrainedError karena pakai `ideal:` bukan `exact:`.
      const cameraConstraints = {
        facingMode: { ideal: "environment" },
        width:  { ideal: 1920 },
        height: { ideal: 1080 },
      };

      // Callback decode error: ZXing memanggil ini setiap frame yang gagal
      //   decode (NotFoundException). Kita bump counter supaya Owner tahu
      //   decoder BENAR-BENAR aktif memproses frame — bukan diam.
      const onDecodeError = (_err) => {
        const now = new Date();
        const hh = String(now.getHours()).padStart(2, "0");
        const mm = String(now.getMinutes()).padStart(2, "0");
        const ss = String(now.getSeconds()).padStart(2, "0");
        bumpDiag({
          decodeErrors: (diagRef.current.decodeErrors || 0) + 1,
          lastErrAt: `${hh}:${mm}:${ss}`,
        });
      };

      // Iter 513 — qrbox tetap longgar (92% x 48% viewport) tetapi fallback ke
      //   full-frame jika helper fn dipanggil dengan dimensi 0 (iOS portrait
      //   kadang reporting salah di frame awal).
      const scanConfig = {
        fps: 20,
        qrbox: (vw, vh) => {
          if (!vw || !vh) return undefined; // let html5-qrcode use full frame
          return {
            width:  Math.floor(Math.min(vw * 0.92, 520)),
            height: Math.floor(Math.min(vh * 0.48, 280)),
          };
        },
        videoConstraints: cameraConstraints,
        disableFlip: true, // 1D barcode: flip tidak diperlukan; back camera non-mirror.
      };

      // Primary attempt: pakai MediaTrackConstraints langsung sebagai `cameraIdOrConfig`.
      // Html5Qrcode support signature: `start(cameraIdOrConfig, config, onSuccess, onError)`.
      try {
        await q.start(cameraConstraints, scanConfig, (decoded) => { sendScan(decoded); }, onDecodeError);
      } catch (primaryErr) {
        // Fallback 1: pakai facingMode saja tanpa width/height constraint.
        try {
          await q.start(
            { facingMode: "environment" },
            { ...scanConfig, videoConstraints: undefined },
            (decoded) => { sendScan(decoded); },
            onDecodeError,
          );
        } catch (fallbackErr) {
          // Fallback 2: ambil kamera pertama dari getCameras() by deviceId.
          const cams = await Html5Qrcode.getCameras().catch(() => []);
          if (!cams || !cams.length) throw primaryErr;
          const back = cams.find(c => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
          await q.start(
            back.id,
            { ...scanConfig, videoConstraints: undefined },
            (decoded) => { sendScan(decoded); },
            onDecodeError,
          );
        }
      }

      // Baca resolusi stream aktual (setelah start) untuk diagnostik.
      try {
        const videoEl = el.querySelector("video");
        const track = videoEl?.srcObject?.getVideoTracks?.()[0];
        const settings = track?.getSettings?.() || {};
        bumpDiag({
          videoW: settings.width || videoEl?.videoWidth || 0,
          videoH: settings.height || videoEl?.videoHeight || 0,
        });
      } catch { /* noop */ }
    } catch (e) {
      setErrMsg(e?.message || "Gagal memulai kamera.");
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { start(); }, 80);
    // Iter 350 — Sembunyikan banner "Pasang TAMFARIS" selama modal scan terbuka
    //   agar tidak menutupi area kamera / hasil scan. Flag dibersihkan di cleanup.
    document.body.classList.add("scan-modal-open");
    return () => {
      clearTimeout(t);
      stop();
      document.body.classList.remove("scan-modal-open");
    };
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;

  const closeAll = async () => { await stop(); onClose?.(); };

  return (
    <div className="scan-mockup-overlay" data-testid="scan-mockup-overlay" role="dialog" aria-modal="true">
      <div className="scan-mockup-sheet">
        <header className="scan-mockup-head">
          <div className="scan-mockup-title">
            <Camera size={18}/> {title || (searchOnly ? "Scan Cari Barang" : "Scan Pindah Tahap")}
          </div>
          <button type="button" className="icon-btn" data-testid="scan-mockup-close" onClick={closeAll} aria-label="Tutup"><X size={18}/></button>
        </header>

        <p className="scan-mockup-hint muted small">
          {searchOnly
            ? <>Arahkan kamera ke barcode mockup untuk mencari pesanan. Hasil scan hanya mengisi kolom pencarian — tidak mengubah status pekerjaan.</>
            : <>Arahkan kamera ke barcode nota. Scan tersimpan sebagai jejak perpindahan tim.</>}
          {operator?.name && <> Scanner: <strong>{operator.name}</strong>{operator.team ? ` · ${operator.team}` : ""}.</>}
          {!operator && user?.name && <> Scanner: <strong>{user.name}</strong>{user.role ? ` · ${user.role}` : ""}.</>}
        </p>

        <div className="scan-mockup-frame">
          <div id={REGION_ID} className="scan-mockup-region"/>
          {!running && !result && (
            <button type="button" className="primary scan-mockup-restart" data-testid="scan-mockup-restart" onClick={start}>
              <Camera size={16}/>{errMsg ? "Coba Lagi" : "Mulai Scan"}
            </button>
          )}
        </div>

        {busy && <div className="scan-mockup-info" data-testid="scan-mockup-busy"><RefreshCw size={14} className="spin"/> {searchOnly ? "Memproses…" : "Mencatat scan…"}</div>}

        {errMsg && (
          <div className="scan-mockup-error" data-testid="scan-mockup-error">
            <AlertCircle size={14}/> {errMsg}
          </div>
        )}

        {/* Iter 512 — Mode search-only: tidak menampilkan ScanResultCard (card
            "Scan tersimpan"); hasil scan langsung mengisi pencarian & menutup modal. */}
        {!searchOnly && result && <ScanResultCard result={result} onAgain={() => { setResult(null); setErrMsg(""); start(); }} onOpenOrder={onOpenOrder} onClose={closeAll}/>}

        {/* Iter 513 — Diagnostik ringan: hanya saat searchOnly. Membantu Owner
            memverifikasi kamera + decoder benar-benar bekerja saat test di iPhone.
            Semua angka tersimpan di memori modal (tidak di-log ke backend). */}
        {searchOnly && running && (
          <div className="scan-mockup-diag" data-testid="scan-mockup-diag"
            style={{marginTop:10, padding:"8px 10px", border:"1px dashed #94a3b8", borderRadius:6, fontSize:12, lineHeight:1.5, color:"#334155", background:"#f8fafc"}}>
            <div><strong>Diagnostik Scanner</strong> <span className="muted" style={{marginLeft:6}}>(hanya tampilan sementara)</span></div>
            <div data-testid="scan-mockup-diag-camera">
              📷 Kamera: {diag.videoW && diag.videoH
                ? <>aktif · {diag.videoW}×{diag.videoH}</>
                : <>menunggu stream…</>}
            </div>
            <div data-testid="scan-mockup-diag-decoder">
              🧠 Decoder: {diag.nativeDetector === "supported"
                  ? <>Native BarcodeDetector (Code 128 OK)</>
                  : diag.nativeDetector === "code128-not-listed"
                  ? <>Native BarcodeDetector ada (tapi Code 128 tidak terdaftar) · fallback ZXing WASM</>
                  : <>ZXing WASM (iPhone/Safari path)</>}
            </div>
            <div data-testid="scan-mockup-diag-formats">
              🎯 Format aktif: <strong>Code 128</strong> (fokus) ·
              {diag.nativeFormats.length
                ? <> native support: {diag.nativeFormats.join(", ")}</>
                : <> native support: —</>}
            </div>
            <div data-testid="scan-mockup-diag-attempts">
              🔁 Frame dicoba decode: <strong>{diag.decodeErrors}</strong> frame
              {diag.lastErrAt && <> · terakhir {diag.lastErrAt}</>}
            </div>
            <div data-testid="scan-mockup-diag-callback">
              ✅ Callback pencarian terpanggil: <strong>{diag.callbackCount}</strong>×
            </div>
            {diag.decodeErrors === 0 && (
              <div style={{color:"#b45309", marginTop:4}}>
                ⚠️ Belum ada frame yang masuk ke decoder — jika ini bertahan &gt; 3 detik,
                izin kamera atau stream mungkin terblokir.
              </div>
            )}
            {diag.decodeErrors > 20 && diag.callbackCount === 0 && (
              <div style={{color:"#b45309", marginTop:4}}>
                ⚠️ Decoder aktif tapi belum menemukan Code 128. Dekatkan barcode
                (±10–15 cm), pastikan garis horizontal & terang. Hindari refleksi.
              </div>
            )}
          </div>
        )}

        {!result && (
          <div className="scan-mockup-manual" data-testid="scan-mockup-manual">
            <p className="muted small">{searchOnly ? "Barcode tidak terbaca? Ketik nomor pesanan:" : "Barcode tidak terbaca? Ketik nomor:"}</p>
            <form onSubmit={(e)=>{ e.preventDefault(); sendScan(manual); setManual(""); }} className="scan-mockup-manual-form">
              <input
                data-testid="scan-mockup-manual-input"
                type="text"
                placeholder="TMF-YYYYMMDD-XXXX atau nomor Shopee"
                value={manual}
                onChange={(e)=>setManual(e.target.value.toUpperCase())}
                autoCapitalize="characters"
                autoCorrect="off"
              />
              <button type="submit" className="primary" data-testid="scan-mockup-manual-submit" disabled={!manual.trim() || busy}>
                <Keyboard size={14}/> {searchOnly ? "Cari Pesanan" : "Catat"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function ScanResultCard({ result, onAgain, onOpenOrder, onClose }) {
  const ctx = result?.context || {};
  const isShopee = result?.source === "shopee";
  const scannedAt = result?.scanned_at || "";
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat("id-ID", { timeZone:"Asia/Jakarta", hour:"2-digit", minute:"2-digit", second:"2-digit", hour12:false }).format(d);
    } catch { return iso; }
  };

  // Resolve "Sekarang di: Tim X · PIC Y"
  let currentWhere = "";
  let currentPic = "";
  if (isShopee) {
    // Mapping ringkas state → tim.
    const ws = ctx.warehouse_state;
    const wp = ctx.warehouse_process_state;
    const bs = ctx.bordir_state;
    const stat = ctx.status;
    if (bs === "forwarded" || bs === "on_hold") currentWhere = "Desain Bordir Shopee";
    else if (ws === "sent" && wp === "processing") currentWhere = "Gudang Shopee · diproses";
    else if (ws === "sent") currentWhere = "Gudang Shopee · menunggu proses";
    else if (stat === "FIX") currentWhere = "Shopee · siap kirim";
    else currentWhere = `Shopee · ${stat || ws || "aktif"}`;
    currentPic = ctx.pic_designer_name || "";
  } else {
    // Offline: pakai product_stage/product_pic jika product_matched, atau snapshot pertama.
    if (ctx.product_matched) {
      currentWhere = ctx.product_stage || "—";
      currentPic = ctx.product_pic || "";
    } else {
      const first = (ctx.products || [])[0];
      currentWhere = first?.stage || ctx.order_status || "—";
      currentPic = first?.pic || "";
      // Jika semua produk pada stage sama, tampilkan tunggal; selainnya ringkas.
      const uniqStages = Array.from(new Set((ctx.products || []).map(p => p.stage))).filter(Boolean);
      if (uniqStages.length > 1) currentWhere = uniqStages.join(" / ");
    }
  }
  const stageIsFinalisasi = !isShopee && ctx.order_status === "Menunggu Finalisasi";
  if (stageIsFinalisasi) currentWhere = "Di Shella (menunggu diterima)";

  return (
    <div className="scan-mockup-result" data-testid="scan-mockup-result">
      <div className="scan-mockup-result-head">
        <CheckCircle2 size={18} color="#15803d"/>
        <div>
          <div className="scan-mockup-result-title">
            {result.deduped ? "Scan sebelumnya (dalam 5 detik)" : "Scan Tercatat"}
          </div>
          <div className="muted small" data-testid="scan-mockup-result-note">Scan hanya mencatat — bukan ACC dan bukan penerusan ke tahap berikutnya.</div>
          <div className="muted small">{fmtTime(scannedAt)} WIB · {isShopee ? "Shopee" : "Offline"}</div>
        </div>
      </div>

      <div className="scan-mockup-result-body">
        <div className="scan-mockup-result-row">
          <span className="eyebrow">Nomor</span>
          <strong data-testid="scan-mockup-result-number">{result.order_number}</strong>
        </div>
        <div className="scan-mockup-result-row">
          <span className="eyebrow">{isShopee ? "Akun" : "Pelanggan"}</span>
          <strong>{isShopee ? (ctx.customer_account || "—") : (ctx.customer_name || "—")}</strong>
        </div>
        {!isShopee && ctx.deadline && (
          <div className="scan-mockup-result-row">
            <span className="eyebrow">Deadline</span>
            <strong>{ctx.deadline}</strong>
          </div>
        )}
        {!isShopee && (
          <div className="scan-mockup-result-row">
            <span className="eyebrow">Status Nota</span>
            <strong>{ctx.order_status || "—"}</strong>
          </div>
        )}
        {isShopee && ctx.design_title && (
          <div className="scan-mockup-result-row">
            <span className="eyebrow">Judul</span>
            <strong>{ctx.design_title}</strong>
          </div>
        )}
        <div className="scan-mockup-result-row scan-mockup-result-where" data-testid="scan-mockup-result-where">
          <span className="eyebrow">Sekarang di</span>
          <strong>{currentWhere || "—"}</strong>
          {currentPic ? <span className="muted"> · Operator <strong>{currentPic}</strong></span> : <span className="muted"> · <em>belum diambil</em></span>}
        </div>
        {isShopee && Array.isArray(ctx.jobs) && ctx.jobs.length > 0 && (
          <div className="scan-mockup-result-products" data-testid="scan-mockup-shopee-jobs">
            <span className="eyebrow">Pekerjaan di nota ini ({ctx.jobs.length})</span>
            <ul>
              {ctx.jobs.map((j, i) => (
                <li key={j.id} data-testid={`scan-mockup-job-${i}`}>
                  <strong>{j.design_title || j.process || "—"}</strong>
                  <span className="muted"> · {j.goods_acc_at ? "ACC Barang Siap" : "Belum di-ACC"}</span>
                  {j.bordir_state ? <span className="chip chip-ok" style={{marginLeft:6}}>Di Desain Bordir</span>
                    : j.bordir_forward_status?.state === "failed" ? <span className="chip" style={{marginLeft:6}} title={j.bordir_forward_status.reason}>Gagal diteruskan</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!isShopee && Array.isArray(ctx.products) && ctx.products.length > 1 && (
          <div className="scan-mockup-result-products">
            <span className="eyebrow">Rincian Produk</span>
            <ul>
              {ctx.products.slice(0, 8).map((p, i) => (
                <li key={i} data-testid={`scan-mockup-prod-${i}`}>
                  <strong>{p.name || "—"}</strong>
                  <span className="muted"> → {p.stage || "—"}</span>
                  {p.pic && <span className="muted"> · {p.pic}</span>}
                  {p.barang_acc_at && <span className="chip chip-ok" style={{marginLeft:6}}>ACC</span>}
                </li>
              ))}
              {ctx.products.length > 8 && <li className="muted small">… {ctx.products.length - 8} produk lain</li>}
            </ul>
          </div>
        )}
      </div>

      <div className="scan-mockup-result-actions">
        <button type="button" className="soft-btn" data-testid="scan-mockup-again" onClick={onAgain}>
          <Camera size={14}/> Scan Lagi
        </button>
        {!result.deduped && !result.source?.startsWith("shopee") && onOpenOrder && (
          <button type="button" className="soft-btn" data-testid="scan-mockup-open-order"
            onClick={async () => {
              try {
                const r = await axios.get(`${API}/orders/${encodeURIComponent(result.order_number)}`, { withCredentials:true });
                onOpenOrder(r.data);
                onClose?.();
              } catch (e) {
                notify.error({ title:"Gagal membuka nota", message: e?.response?.data?.detail || "Silakan coba lagi." });
              }
            }}>
            Buka Detail Nota
          </button>
        )}
        <button type="button" className="primary" data-testid="scan-mockup-close-done" onClick={onClose}>
          Selesai
        </button>
      </div>
    </div>
  );
}
